import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { showToast } from "../toastService";
import "./css/WorkCard.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function WorkCard({ work, lang = "en" }) {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(work.is_favorite || false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // Translations
  const t = {
    en: {
      description: "Description",
      categories: "Categories",
      specialisations: "Specialisations",
      knowMore: "Know more",
      allCategories: "All Categories",
      close: "Close",
      quoteSubmitted: "Quote Submitted",
      quoteApproved: "Quote Approved",
      quoteRejected: "Quote Rejected",
    },
    ar: {
      description: "الوصف",
      categories: "الفئات",
      specialisations: "التخصصات",
      knowMore: "اعرف المزيد",
      allCategories: "جميع الفئات",
      close: "إغلاق",
      quoteSubmitted: "تم تقديم العرض",
      quoteApproved: "تم قبول العرض",
      quoteRejected: "تم رفض العرض",
    },
  }[lang];

  // Get quote status badge info
  const getQuoteStatusBadge = () => {
    if (!work.latest_quote || !work.latest_quote.status) return null;
    
    const status = work.latest_quote.status.toLowerCase();
    if (status === "pending") {
      return {
        text: t.quoteSubmitted,
        bg: "#47c0c9",
        textColor: "#fff",
      };
    } else if (status === "approved") {
      return {
        text: t.quoteApproved,
        bg: "#0AC26F",
        textColor: "#fff",
      };
    } else if (status === "rejected") {
      return {
        text: t.quoteRejected,
        bg: "#FF5252",
        textColor: "#fff",
      };
    }
    return null;
  };

  const quoteStatusBadge = getQuoteStatusBadge();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount);
  };

  // Get category name
  const getCategoryName = () => {
    if (work.category_id && typeof work.category_id === "object") {
      return work.category_id.name || "";
    }
    return "";
  };

  // Get category badge color based on category name
  const getCategoryBadgeColor = (categoryName) => {
    if (!categoryName) return null;
    const name = categoryName.toLowerCase();
    if (name.includes("construction")) {
      return { bg: "#47c0c9", text: "#fff" }; // Teal
    } else if (name.includes("maintenance")) {
      return { bg: "#10b981", text: "#fff" }; // Green
    } else if (name.includes("material") || name.includes("supply")) {
      return { bg: "#6b7280", text: "#fff" }; // Grey
    }
    return { bg: "#e5e7eb", text: "#374151" }; // Default grey
  };

  const categoryName = getCategoryName();
  const badgeColor = getCategoryBadgeColor(categoryName);

  // Get location string
  const getLocation = () => {
    const parts = [];
    if (work.address1) parts.push(work.address1);
    if (work.city) parts.push(work.city);
    if (work.state) parts.push(work.state);
    if (work.pincode) parts.push(`- ${work.pincode}`);
    return parts.join(", ");
  };

  // Get sub-categories display
  const getSubCategoriesDisplay = () => {
    if (!work.sub_categories || work.sub_categories.length === 0) return [];
    return work.sub_categories.map((sc) =>
      typeof sc === "object" ? sc.name : sc
    );
  };

  const subCategories = getSubCategoriesDisplay();
  const specialisations =
    (work.specialisations || []).map((s) =>
      typeof s === "object" ? s.name : s
    ) || [];
  const showMoreCount = subCategories.length > 2 ? subCategories.length - 2 : 0;

  // Handle favorite toggle
  const handleFavoriteToggle = async () => {
    if (!work._id) {
      showToast("Work ID not found", "error");
      return;
    }

    const newFavoriteStatus = !isFavorite;
    setFavoriteLoading(true);

    try {
      const response = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/add-update-favorites/`,
        "POST",
        JSON.stringify({
          id: work._id,
          is_favorite: newFavoriteStatus,
        }),
        { "Content-Type": "application/json" }
      );

      if (response && response.response_code === 200) {
        setIsFavorite(newFavoriteStatus);
        showToast(
          newFavoriteStatus
            ? "Added to favorites"
            : "Removed from favorites",
          "success"
        );
      } else {
        showToast(response?.message || "Failed to update favorite", "error");
      }
    } catch (error) {
      console.error("Error updating favorite:", error);
      showToast(
        error.message || "Failed to update favorite. Please try again.",
        "error"
      );
      // Revert the state on error
      setIsFavorite(!newFavoriteStatus);
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <div className="work-card" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="work-card-header">
        <div className="work-card-title-container">
          <h3 className="work-card-title">
            {work.title || categoryName}
          </h3>
          {work.title && categoryName && badgeColor && (
            <span
              className="work-card-category-badge"
              style={{
                backgroundColor: badgeColor.bg,
                color: badgeColor.text,
              }}
            >
              {categoryName}
            </span>
          )}
        </div>
        <button
          className={`work-favorite-btn ${isFavorite ? "active" : ""}`}
          onClick={handleFavoriteToggle}
          disabled={favoriteLoading}
        >
          <i className={`bx ${isFavorite ? "bxs-heart" : "bx-heart"}`}></i>
        </button>
      </div>

      <div className="work-card-body">
        {/* Location */}
        <div className="work-info-row">
          <i className="bx bx-map"></i>
          <span className="work-info-text">{getLocation()}</span>
        </div>

        {/* Dates */}
        <div className="work-info-row">
          <i className="bx bx-calendar"></i>
          <span className="work-info-text">
            {work.created_at && (
              <>
                <b>Post Date:</b> {formatDate(work.created_at)}
                {work.quote_last_date && (
                  <>  &nbsp; <b>End Date:</b> {formatDate(work.quote_last_date)}</>
                )}
              </>
            )}
          </span>
        </div>

        {/* Amount */}
        {work.work_amount && (
          <div className="work-info-row">
            <i className="bx bx-money"></i>
            <span className="work-info-text">
              AED {formatAmount(work.work_amount.min || 0)} - AED{" "}
              {formatAmount(work.work_amount.max || 0)}
            </span>
          </div>
        )}

        {/* Description */}
        {work.description && (
          <div className="work-description-section">
            <h4 className="work-section-title">{t.description}</h4>
            <p className="work-description-text">{work.description}</p>
          </div>
        )}

        {/* Categories */}
        {subCategories.length > 0 && (
          <div className="work-categories-section">
            <h4 className="work-section-title">{t.categories}</h4>
            <div className="work-categories-list">
              {subCategories.slice(0, 2).map((cat, index) => (
                <span key={index} className="work-category-chip">
                  {cat}
                </span>
              ))}
              {showMoreCount > 0 && (
                <span
                  className="work-category-chip more"
                  onClick={() => setShowCategoriesModal(true)}
                  style={{ cursor: "pointer" }}
                >
                  +{showMoreCount} More
                </span>
              )}
            </div>
          </div>
        )}

        {/* Specialisations */}
        {specialisations.length > 0 && (
          <div className="work-categories-section">
            <h4 className="work-section-title">{t.specialisations}</h4>
            <div className="work-categories-list">
              {specialisations.map((spec, index) => (
                <span key={index} className="work-category-chip">
                  {spec}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="work-card-footer">
        {quoteStatusBadge && (
          <span
            className="work-quote-status-badge"
            style={{
              backgroundColor: quoteStatusBadge.bg,
              color: quoteStatusBadge.textColor,
            }}
          >
            {quoteStatusBadge.text}
          </span>
        )}
        <button 
          className="work-know-more-link"
          onClick={() => navigate(`/work/${work._id}`)}
        >
          {t.knowMore} &gt;
        </button>
      </div>

      {/* Categories Modal */}
      {showCategoriesModal && (
        <div
          className="categories-modal-overlay"
          onClick={() => setShowCategoriesModal(false)}
        >
          <div
            className="categories-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="categories-modal-header">
              <h3>{t.allCategories}</h3>
              <button
                className="categories-modal-close"
                onClick={() => setShowCategoriesModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="categories-modal-body">
              {subCategories.map((cat, index) => (
                <span key={index} className="work-category-chip">
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
